/* eslint-disable react/jsx-no-bind */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { Keys } from '../../lib/keyboard';
require("./PinInput.css");
var styles = {
  "Root": "PinInput-module-Root_1WQ",
  "Content": "PinInput-module-Content__5m",
  "Hint": "PinInput-module-Hint_3yh",
  "Field": "PinInput-module-Field_xwh",
  "disabled": "PinInput-module-disabled_3HT",
  "Item": "PinInput-module-Item_7p3",
  "cursor-blink": "PinInput-module-cursor-blink_2qv",
  "active": "PinInput-module-active_3-F",
  "Control": "PinInput-module-Control_3wb",
  "errorState": "PinInput-module-errorState_2xt"
};
export const PinInput = ({
  initValue,
  length = 4,
  regExp = '[0-9]',
  disabled = false,
  autofocus = false,
  state,
  className,
  onComplete,
  onFocus,
  hint,
  style,
  innerRef,
  controlRef
}) => {
  const [value, setValue] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputItemsRef = useMemo(() => new Array(length).fill(null).map(() => /*#__PURE__*/React.createRef()), [length]);
  const valueRegExp = useMemo(() => new RegExp(regExp), [regExp]);
  useEffect(() => {
    if (!initValue || !valueRegExp.test(initValue)) return;
    setValue(initValue.slice(0, length).split(''));
  }, [initValue, length, valueRegExp]);
  useEffect(() => {
    const resultValue = value.join('');
    if (onComplete && resultValue.length === length) {
      onComplete(resultValue);
    }
  }, [value, onComplete, length]);
  useEffect(() => {
    if (autofocus && inputItemsRef[0].current) {
      inputItemsRef[0].current.focus();
    }
  }, [autofocus, inputItemsRef]);
  const getInputItem = useCallback(index => inputItemsRef[index]?.current, [inputItemsRef]);
  const focusInputItem = useCallback(index => {
    getInputItem(index)?.focus();
  }, [getInputItem]);
  const activateInputItemByIndex = useCallback(index => {
    setActiveIndex(index);
    focusInputItem(index);
  }, [focusInputItem]);
  const handleFocus = e => {
    setActiveIndex(Number(e.currentTarget.dataset.index));
    if (onFocus) {
      onFocus();
    }
  };
  const onKeyUp = useCallback(({
    key
  }) => {
    const newValue = [...value];
    const nextIndex = activeIndex + 1;
    const prevIndex = activeIndex - 1;
    const isLastIndex = nextIndex === length;
    const currentItem = getInputItem(activeIndex);
    if (key === Keys.DELETE || key === Keys.BACKSPACE) {
      newValue[activeIndex] = '';
      setValue(newValue);
      if (activeIndex > 0) {
        activateInputItemByIndex(prevIndex);
      }
      return;
    } else if (key === Keys.LEFT) {
      if (activeIndex > 0) {
        activateInputItemByIndex(prevIndex);
        return;
      }
    } else if (key === Keys.RIGHT) {
      if (activeIndex < length - 1) {
        activateInputItemByIndex(nextIndex);
        return;
      }
    }
    if (!valueRegExp.test(key) || key.length > 1) return;
    newValue[activeIndex] = key;
    setValue(newValue);
    if (!isLastIndex) {
      activateInputItemByIndex(nextIndex);
      return;
    }
    if (currentItem) currentItem.blur();
    setActiveIndex(-1);
  }, [value, activateInputItemByIndex, getInputItem, activeIndex, length, valueRegExp]);
  const onBlur = useCallback(() => {
    if (activeIndex === -1) return;
    setActiveIndex(-1);
  }, [activeIndex]);
  const onPaste = useCallback(e => {
    e.preventDefault();
    const pastedString = e.clipboardData?.getData('text');
    if (!pastedString || !valueRegExp.test(pastedString)) return;
    setValue(pastedString.slice(0, length).split(''));
  }, [valueRegExp, length]);
  const onChange = useCallback(e => {
    e.preventDefault();
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    ref: innerRef,
    className: cn(styles.Root, styles[`${state}State`], {
      [styles.disabled]: disabled
    }, [className]),
    style: style
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.Content
  }, inputItemsRef.map((inputItem, index) => {
    return /*#__PURE__*/React.createElement("div", {
      key: index,
      className: cn(styles.Item, {
        [styles.active]: activeIndex === index,
        [styles.filled]: Boolean(value[index])
      })
    }, /*#__PURE__*/React.createElement("input", {
      ref: controlRef || inputItem,
      type: "text",
      autoComplete: "off",
      "aria-label": `Введите символ кода ${index + 1} из ${length}`,
      maxLength: 1,
      disabled: disabled,
      "data-index": index,
      "aria-invalid": state === 'error',
      onFocus: handleFocus,
      onBlur: onBlur,
      onKeyUp: onKeyUp,
      onPaste: onPaste,
      onChange: onChange,
      className: styles.Field,
      value: value[index] || ''
    }));
  })), hint && /*#__PURE__*/React.createElement("p", {
    className: styles.Hint
  }, hint));
};
if (process.env.NODE_ENV !== 'production') {
  PinInput.displayName = 'PinInput';
}